import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { AxiosError } from 'axios'

import { apiZodios } from '~api/apiZodios'
import { Button, TextField, Typography } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { type TwilioAccountConfig } from '~types'

import { useAISettings } from '../../AISettingsContext'

import './TwilioAccountSection.scss'
import { TWILIO_SID_RULES, validateTwilioSid } from './utils'

interface TwilioAccountFormProps {
  onSuccess: (message: string) => void
  onCancel?: () => void
  twilioAccountConfig?: TwilioAccountConfig
}

type SidField = keyof typeof TWILIO_SID_RULES

const TwilioAccountForm: React.FC<TwilioAccountFormProps> = ({
  onSuccess,
  onCancel,
  twilioAccountConfig,
}) => {
  const { selectedOrganization } = useAISettings()
  const user = useSelector((state: RootState) => state?.auth.user)
  const isUpdate = !!twilioAccountConfig

  const [name, setName] = useState<string>(twilioAccountConfig?.name ?? '')
  const [sids, setSids] = useState<Record<SidField, string>>({
    accountSid: twilioAccountConfig?.account_sid ?? '',
    messagingServiceSid: twilioAccountConfig?.messaging_service_sid ?? '',
    phonicTrunkSid: twilioAccountConfig?.phonic_trunk_sid ?? '',
  })
  const [touched, setTouched] = useState<Record<SidField | 'name', boolean>>({
    name: false,
    accountSid: false,
    messagingServiceSid: false,
    phonicTrunkSid: false,
  })
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const nameError = name.trim() ? null : 'Name is required'
  const sidErrors: Record<SidField, string | null> = {
    accountSid: validateTwilioSid(sids.accountSid, TWILIO_SID_RULES.accountSid),
    messagingServiceSid: validateTwilioSid(
      sids.messagingServiceSid,
      TWILIO_SID_RULES.messagingServiceSid,
    ),
    phonicTrunkSid: validateTwilioSid(sids.phonicTrunkSid, TWILIO_SID_RULES.phonicTrunkSid),
  }
  const isValid = !nameError && Object.values(sidErrors).every((sidError) => !sidError)

  const handleSidChange = (field: SidField, value: string): void => {
    setSids((prev) => ({ ...prev, [field]: value.trim() }))
  }

  const markTouched = (field: SidField | 'name'): void => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  const handleSave = async (): Promise<void> => {
    if (!selectedOrganization?.id || !user || !isValid) return

    const fields = {
      user_id: user.id,
      organization_identity_id: selectedOrganization.id,
      name: name.trim(),
      account_sid: sids.accountSid,
      messaging_service_sid: sids.messagingServiceSid || null,
      phonic_trunk_sid: sids.phonicTrunkSid || null,
    }

    setIsSaving(true)
    setError(null)
    try {
      if (twilioAccountConfig) {
        await apiZodios.integrations_editTwilioAccountConfig({
          ...fields,
          twilio_account_config_id: twilioAccountConfig.id,
        })
        onSuccess('Twilio account updated successfully')
      } else {
        await apiZodios.integrations_addTwilioAccountConfig(fields)
        onSuccess('Twilio account added successfully')
      }
    } catch (err: unknown) {
      console.error('Error saving Twilio account:', err)
      const detail =
        err instanceof AxiosError ? (err.response?.data as { detail?: string })?.detail : null
      setError(detail || 'Failed to save Twilio account. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const renderSidField = (field: SidField, placeholder: string): React.ReactNode => {
    const rule = TWILIO_SID_RULES[field]
    const showError = touched[field] && !!sidErrors[field]
    return (
      <TextField
        label={rule.required ? `${rule.label} *` : rule.label}
        placeholder={placeholder}
        value={sids[field]}
        onChange={(e) => handleSidChange(field, e.target.value)}
        onBlur={() => markTouched(field)}
        error={showError}
        helperText={showError ? (sidErrors[field] ?? undefined) : undefined}
      />
    )
  }

  return (
    <Box className='twilio-account-form'>
      <Box>
        <Typography variant='subtitle2'>
          {isUpdate ? 'Edit Twilio Account' : 'Add Twilio Account'}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          SIDs can be found in the Twilio console.
        </Typography>
      </Box>
      {error && (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      )}
      <TextField
        label='Name *'
        placeholder='e.g. Public Works Sub-Account'
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => markTouched('name')}
        error={touched.name && !!nameError}
        helperText={touched.name && nameError ? nameError : undefined}
      />
      {renderSidField('accountSid', 'AC…')}
      {renderSidField('messagingServiceSid', 'MG…')}
      {renderSidField('phonicTrunkSid', 'TK…')}
      <Box className='twilio-account-form__actions'>
        {onCancel && (
          <Button variant='outlined' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button variant='contained' onClick={handleSave} disabled={!isValid || isSaving}>
          {isUpdate ? 'Update Twilio Account' : 'Add Twilio Account'}
        </Button>
      </Box>
    </Box>
  )
}

export default TwilioAccountForm
